import { supabase } from '../lib/supabaseClient';

export interface Subscription {
    id: string;
    user_id: string;
    plan: 'free' | 'pro' | 'enterprise';
    status: 'active' | 'expired' | 'cancelled';
    starts_at: string;
    expires_at: string | null;
    ai_credits_used: number;
    ai_credits_limit: number;
}

export interface AiBreakdownAllowance {
    is_paid: boolean;
    limit: number;
    used: number;
    remaining: number;
}

// In-memory cache to avoid redundant queries
let cachedSubscription: Subscription | null = null;
let cachedUserId: string | null = null;
let cachedAt = 0;
let generation = 0;
export const SUBSCRIPTION_CACHE_MS = 60_000;

export const subscriptionService = {
    // Always read the server ledger: deleted projects and expired job rows still
    // count, and other tabs may have accepted an upload since the previous read.
    getAiBreakdownAllowance: async (): Promise<AiBreakdownAllowance> => {
        const { data, error } = await supabase.rpc('get_ai_breakdown_allowance');
        if (error || !data || typeof data.is_paid !== 'boolean' ||
            ![data.limit, data.used, data.remaining].every(value => Number.isInteger(value) && value >= 0)) {
            throw new Error('Could not check your free AI projects. Please try again.');
        }
        return data as AiBreakdownAllowance;
    },

    /**
     * Fetch the subscription for a given user.
     * Returns null if no subscription row exists (legacy users).
     */
    getSubscription: async (userId: string): Promise<Subscription | null> => {
        // Return cache if same user
        if (cachedUserId === userId && cachedSubscription && Date.now() - cachedAt < SUBSCRIPTION_CACHE_MS) {
            return cachedSubscription;
        }

        const requestGeneration = ++generation;
        const { data, error } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle();

        if (error) throw new Error('Your plan could not be loaded. Please retry.');
        if (requestGeneration !== generation) return data as Subscription | null;
        if (!data) {
            cachedSubscription = null;
            cachedUserId = userId;
            return null;
        }

        cachedSubscription = data as Subscription;
        cachedUserId = userId;
        cachedAt = Date.now();
        return cachedSubscription;
    },

    /**
     * Check if a user has an active paid subscription.
     * Returns true if plan is NOT 'free', status is 'active',
     * and the subscription has not expired.
     */
    hasActiveSubscription: async (userId: string): Promise<boolean> => {
        const sub = await subscriptionService.getSubscription(userId);
        if (!sub) return false;
        if (sub.plan === 'free') return false;
        if (sub.status !== 'active') return false;
        if (sub.expires_at && new Date(sub.expires_at) < new Date()) return false;
        return true;
    },

    /**
     * Clear the cache (call on sign-out or when subscription changes).
     */
    clearCache: () => {
        generation += 1;
        cachedSubscription = null;
        cachedUserId = null;
        cachedAt = 0;
    },

    /**
     * Force re-fetch from DB (e.g. after admin grants a subscription).
     */
    refreshSubscription: async (userId: string): Promise<Subscription | null> => {
        subscriptionService.clearCache();
        return subscriptionService.getSubscription(userId);
    },
};
